import React, { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import About from "./pages/About";
import Services from "./pages/Services";
import Contact from "./pages/Contact";
import Blog from "./pages/Blog";
import BlogForm from "./pages/BlogForm";
import AdminPanel from "./pages/AdminPanel"; // Import AdminPanel
import "./styles/global.css";
import MobileArrow from "./components/MobileArrow";

// Wrapper component to handle navigation with transition
function AppContent() {
  const [transitioning, setTr] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const cursorRef = useRef(null);
  const cursorRingRef = useRef(null);

  // Custom cursor
  useEffect(() => {
    const move = (e) => {
      if (cursorRef.current) {
        cursorRef.current.style.left = e.clientX + "px";
        cursorRef.current.style.top = e.clientY + "px";
      }
      if (cursorRingRef.current) {
        setTimeout(() => {
          cursorRingRef.current.style.left = e.clientX + "px";
          cursorRingRef.current.style.top = e.clientY + "px";
        }, 80);
      }
    };

    window.addEventListener("mousemove", move);
    return () => window.removeEventListener("mousemove", move);
  }, []);

  // Navigation with transition
  const navigateWithTransition = (target) => {
    const currentPath = location.pathname.substring(1) || "home";
    if (target === currentPath) return;

    setTr(true);

    setTimeout(() => {
      navigate(target === "home" ? "/" : `/${target}`);

      setTimeout(() => {
        window.scrollTo(0, 0);
        setTimeout(() => {
          setTr(false);
        }, 100);
      }, 100);
    }, 500);
  };

  // Get current page name from path
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === "/" || path === "/home") return "home";
    if (path === "/about") return "about";
    if (path === "/services") return "services";
    if (path === "/contact") return "contact";
    if (path === "/blog") return "blog";
    if (path === "/admin") return "admin";
    if (path === "/blog/new") return "blog";
    if (path.match(/^\/blog\/edit\/\d+$/)) return "blog";
    return "home";
  };

  // WhatsApp click handler
  const openWhatsApp = () => {
    const phoneNumber = "919789461160";
    const message = "Hello! I'm interested in your HR solutions";
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, "_blank");
  };

  // Check if current route is admin or blog form (to hide/show certain elements)
  const isAdminRoute = location.pathname === "/admin";
  const isBlogFormRoute =
    location.pathname === "/blog/new" ||
    location.pathname.match(/^\/blog\/edit\/\d+$/);
  const isFullPageRoute = isAdminRoute || isBlogFormRoute;

  return (
    <>
      {/* Custom Cursor - Hide on admin and form pages for better UX */}
      {!isFullPageRoute && (
        <>
          <div className="cursor" ref={cursorRef} />
          <div className="cursor-ring" ref={cursorRingRef} />
        </>
      )}

      {/* Page Transition */}
      <div className={`transition-wrapper ${transitioning ? "active" : ""}`}>
        <div className="layer layer-black"></div>
        <div className="layer layer-gold"></div>
        <div className="layer layer-white"></div>
      </div>

      {/* Layout - Hide Navbar and Footer on admin and form pages */}
      {!isFullPageRoute && (
        <Navbar
          currentPage={getCurrentPage()}
          onNavigate={navigateWithTransition}
        />
      )}

      <main className={isFullPageRoute ? "full-page-main" : ""}>
        <Routes>
          {/* Main Pages */}
          <Route
            path="/"
            element={<Home onNavigate={navigateWithTransition} />}
          />
          <Route
            path="/home"
            element={<Home onNavigate={navigateWithTransition} />}
          />
          <Route
            path="/about"
            element={<About onNavigate={navigateWithTransition} />}
          />
          <Route
            path="/services"
            element={<Services onNavigate={navigateWithTransition} />}
          />
          <Route
            path="/contact"
            element={<Contact onNavigate={navigateWithTransition} />}
          />
          <Route
            path="/blog"
            element={<Blog onNavigate={navigateWithTransition} />}
          />

          {/* Admin and Blog Management Routes */}
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/blog/new" element={<BlogForm />} />
          <Route path="/blog/edit/:id" element={<BlogForm />} />
        </Routes>
      </main>

      {/* Footer - Hide on admin and form pages */}
      {!isFullPageRoute && <Footer onNavigate={navigateWithTransition} />}

      {/* Mobile Arrow - Hide on admin and form pages */}
      {!isFullPageRoute && <MobileArrow />}

      {/* WhatsApp Icon - Hide on admin and form pages */}
      {!isFullPageRoute && (
        <div className="whatsapp-icon" onClick={openWhatsApp}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 448 512"
            width="30"
            height="30"
            fill="currentColor"
          >
            <path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.2-17.2-41.3-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z" />
          </svg>
        </div>
      )}
    </>
  );
}

// Main App component with Router
export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}
